import { useState, useEffect } from "react";
import {
  Card, Table, Stack, TableRow, TableBody, TableCell, Container, Typography,
  TableContainer, TextField, Grid, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import Page from "src/components/Page";
import Scrollbar from "src/components/Scrollbar";
import SearchNotFound from "src/components/SearchNotFound";
import ListHead from "src/components/common/ListHead";
import ListToolbar from "src/components/common/ListToolbar";
import MyPagination from "src/components/common/MyPagination";
import { asyncRequest } from "src/services/network";
import { alert } from "src/services/alert";
import Confirm from "src/components/common/ConfirmDialog";

const TABLE_HEAD = [
  { id: "id", label: "ID", alignRight: false },
  { id: "grade_name", label: "等级名称", alignRight: false },
  { id: "min_dhz_amount", label: "最小金豆", alignRight: false },
  { id: "max_dhz_amount", label: "最大金豆", alignRight: false },
  { id: "min_dragon_amount", label: "最小银豆", alignRight: false },
  { id: "max_dragon_amount", label: "最大银豆", alignRight: false },
  { id: "income_ratio", label: "收益比例", alignRight: false },
  { id: "benefit_days", label: "受益天数", alignRight: false },
  { id: "action", label: "操作", alignRight: false },
];

const DEFAULT_FORM = {
  grade_name: "",
  min_dhz_amount: "",
  max_dhz_amount: "",
  min_dragon_amount: "",
  max_dragon_amount: "",
  dhz_fee_ratio: "",
  dragon_fee_ratio: "",
  income_ratio: "",
  benefit_days: "",
  repayment_days: "",
  contribution_delivery_percentage: "",
  dragon_percentage: "",
  dragon_output_days: "",
};

export default function GradeList() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [hasQueried, setHasQueried] = useState(false);
  const [filter, setFilter] = useState({ page: 0, size: 20, grade_name: "" });

  const [editOpen, setEditOpen] = useState(false);
  const [editMode, setEditMode] = useState("create");
  const [editInitial, setEditInitial] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  async function getItems(filterOverride) {
    setItems(null);
    const q = filterOverride || filter;
    const payload = { page: q.page, size: q.size };
    if (q.grade_name) payload.grade_name = q.grade_name;

    const { data, count } = await asyncRequest({ path: "/jingxi/grade/list", data: payload });
    setItems(data || []);
    setTotal(count || 0);
    setHasQueried(true);
  }

  const handlePageChange = (event, value, newSize) => {
    let newFilter;
    if (newSize !== undefined) {
      newFilter = { ...filter, page: 0, size: newSize };
    } else {
      const newPage = value - 1;
      if (newPage === filter.page) return;
      newFilter = { ...filter, page: newPage };
    }
    setFilter(newFilter);
    if (hasQueried) getItems(newFilter);
  };

  function handleReset() {
    setHasQueried(false);
    setItems(null);
    setTotal(0);
    setFilter({ page: 0, size: 20, grade_name: "" });
  }

  function openCreate() {
    setEditMode("create");
    setEditInitial(null);
    setForm(DEFAULT_FORM);
    setEditOpen(true);
  }

  function openEdit(row) {
    setEditMode("edit");
    setEditInitial(row);
    setForm({ ...DEFAULT_FORM, ...row });
    setEditOpen(true);
  }

  async function handleDelete(row) {
    Confirm({
      message: `确认删除等级"${row.grade_name}"？删除后不可恢复。`,
      async onConfirm() {
        try {
          const res = await asyncRequest({
            path: "/jingxi/grade/delete",
            data: { id: row.id },
          });
          if (res.code === 1) {
            alert("删除成功", "success");
            getItems();
          } else {
            alert(res.msg || "删除失败");
          }
        } catch (err) {
          alert("删除失败");
        }
      },
    });
  }

  const setFormField = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setNumField = (k) => (e) => {
    const v = e.target.value;
    setForm({ ...form, [k]: v === "" ? "" : Number(v) });
  };

  async function handleSubmit() {
    if (!form.grade_name) {
      return alert("请填写等级名称");
    }

    setSaving(true);
    try {
      const path = editMode === "edit" ? "/jingxi/grade/update" : "/jingxi/grade/create";
      const payload = editMode === "edit" ? { id: editInitial.id, ...form } : form;
      const res = await asyncRequest({ path, data: payload });
      if (res.code === 1) {
        alert(editMode === "edit" ? "更新成功" : "创建成功", "success");
        getItems();
        setEditOpen(false);
      } else {
        alert(res.msg || "保存失败");
      }
    } catch (err) {
      alert("保存失败");
    } finally {
      setSaving(false);
    }
  }

  const isNotFound = items && items.length === 0;

  return (
    <Page title="金银通用户等级管理">
      <Container maxWidth="2000px">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>用户等级管理</Typography>
          <Button variant="contained" onClick={openCreate}>新建等级</Button>
        </Stack>

        <Card>
          <ListToolbar showSearch={false} onReset={handleReset} onRefresh={() => getItems()}>
            <Grid container rowGap={2} alignItems="center">
              <Grid item sm={6} md={4}>
                <TextField size="small" style={{ width: 200 }} label="等级名称"
                  placeholder="输入等级名称" autoComplete="off"
                  value={filter.grade_name} onChange={(e) => setFilter({ ...filter, grade_name: e.target.value })} />
              </Grid>
              <Grid item sm={6} md={2}>
                <Button variant="contained" size="small" onClick={() => getItems()}>查询</Button>
              </Grid>
            </Grid>
          </ListToolbar>

          <Scrollbar>
            <TableContainer sx={{ minWidth: 1200 }}>
              <Table>
                <ListHead headLabel={TABLE_HEAD} />
                {!hasQueried ? (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Stack sx={{ width: "100%", height: 200 }} alignItems="center" justifyContent="center">
                          <Typography variant="body2" color="text.secondary">请点击查询加载数据</Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : !items ? (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Stack sx={{ width: "100%", height: 200 }} alignItems="center" justifyContent="center">加载中...</Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : (
                  <TableBody>
                    {items.map((row) => (
                      <TableRow hover key={row.id} tabIndex={-1}>
                        <TableCell align="left">{row.id}</TableCell>
                        <TableCell align="left">{row.grade_name}</TableCell>
                        <TableCell align="left">{row.min_dhz_amount ?? "-"}</TableCell>
                        <TableCell align="left">{row.max_dhz_amount ?? "-"}</TableCell>
                        <TableCell align="left">{row.min_dragon_amount ?? "-"}</TableCell>
                        <TableCell align="left">{row.max_dragon_amount ?? "-"}</TableCell>
                        <TableCell align="left">{row.income_ratio ?? "-"}</TableCell>
                        <TableCell align="left">{row.benefit_days ?? "-"}</TableCell>
                        <TableCell align="left">
                          <Stack direction="row" spacing={1}>
                            <Button size="small" variant="outlined" onClick={() => openEdit(row)}>编辑</Button>
                            <Button size="small" variant="outlined" color="error" onClick={() => handleDelete(row)}>删除</Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                )}
                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={9} sx={{ py: 5 }}><SearchNotFound /></TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <MyPagination total={total} page={filter.page} size={filter.size} onChange={handlePageChange} />
        </Card>
      </Container>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editMode === "edit" ? "编辑等级" : "新建等级"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="等级名称 *" value={form.grade_name} onChange={setFormField("grade_name")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="number" label="最小金豆金额" value={form.min_dhz_amount} onChange={setNumField("min_dhz_amount")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="number" label="最大金豆金额" value={form.max_dhz_amount} onChange={setNumField("max_dhz_amount")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="number" label="最小银豆" value={form.min_dragon_amount} onChange={setNumField("min_dragon_amount")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="number" label="最大银豆" value={form.max_dragon_amount} onChange={setNumField("max_dragon_amount")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="number" inputProps={{ step: 0.001 }} label="金豆手续费比例" value={form.dhz_fee_ratio} onChange={setNumField("dhz_fee_ratio")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="number" inputProps={{ step: 0.001 }} label="银豆手续费" value={form.dragon_fee_ratio} onChange={setNumField("dragon_fee_ratio")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="number" inputProps={{ step: 0.001 }} label="收益比例" value={form.income_ratio} onChange={setNumField("income_ratio")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="number" label="受益天数" value={form.benefit_days} onChange={setNumField("benefit_days")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="number" label="回本天数" value={form.repayment_days} onChange={setNumField("repayment_days")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="number" inputProps={{ step: 0.001 }} label="贡献值交割百分比" value={form.contribution_delivery_percentage} onChange={setNumField("contribution_delivery_percentage")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="number" inputProps={{ step: 0.001 }} label="高级神龙百分比" value={form.dragon_percentage} onChange={setNumField("dragon_percentage")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="number" label="神龙产出天数" value={form.dragon_output_days} onChange={setNumField("dragon_output_days")} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>取消</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? "保存中..." : "保存"}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
